/**
 * Contacts client (LF-3215 / LF-3216)
 *
 * Browser-side helpers backed by `directApiClient`. Both reads (LF-3215)
 * and mutations (LF-3216) live here. Server-Component reads use the
 * `fetch*` helpers in `lib/data/matter-reads.ts` — do not import from
 * here in Server Components.
 *
 * Cache invalidation for mutations is handled at the hook layer via
 * `getDependencies('contact', matterId)` (or `'contact-verification'` for
 * the verification surface).
 */

import { directApiClient, DirectApiError } from '@/lib/api/direct-client';
import {
  Contact,
  ContactListResponse,
  GlobalContact,
  BankVerificationDetails,
  CreateContactRequest,
  UpdateContactRequest,
  ContactActionResult,
  ErrorWithValidation,
  CreateContactRequestSchema,
  UpdateContactRequestSchema,
  RequestBankVerificationResponse,
  ResendVerificationLinkResponse,
  BeneficiaryEmailOverride,
  BeneficiaryAttachmentSelection,
  PreviewBeneficiaryEmailResponse,
  ApproveBankVerificationRequest,
  ApproveBankVerificationResponse,
  ConfirmContactIdentityRequest,
  ContactMergePreview,
  ContactMergeResult,
  ContactDuplicateCandidate
} from './types';
import { formatDateForApi } from '@/lib/utils/date-format';

function toErrorResult<T>(error: unknown): ContactActionResult<T> {
  if (error instanceof DirectApiError) {
    /*
      **Carry the status and the body, don't drop them** (VALE-349) — see
      {@link ContactActionResult.status}. Returning only `error.message` is what
      made `saveFailureMessage`'s status-driven branches unreachable on every
      contacts surface, so a dropped connection reached the reader as
      `Failed to fetch` and a class-validator 400 as a database column name.
    */
    return {
      success: false,
      error: error.message,
      status: error.status,
      response: error.response
    };
  }
  return {
    success: false,
    error: error instanceof Error ? error.message : 'Request failed'
  };
}

/**
 * **Turn a failed {@link ContactActionResult} back into the error it came from**
 * (VALE-349).
 *
 * The seven mutation hooks in `./hooks` each rebuilt an `Error` from
 * `result.error` alone, which is the step that lost the status. This is that
 * step, written once: a failure that came from the API is rethrown as the
 * `DirectApiError` it was, so anything downstream that reads a status —
 * `handleMutationError` for a toast, `saveFailureMessage` for an attribute row —
 * sees what it was written to see.
 *
 * A failure with no status never came from the API (a schema refusal before the
 * request, a thrown non-`Error`), so it stays a plain `Error`: inventing a
 * status for it would be a second lie in place of the first.
 */
export function toActionError(
  result: Pick<
    ContactActionResult<unknown>,
    'error' | 'status' | 'response' | 'validationErrors'
  >,
  fallbackMessage: string
): Error {
  const message = result.error || fallbackMessage;
  const error = (
    result.status === undefined
      ? new Error(message)
      : new DirectApiError(message, result.status, result.response)
  ) as ErrorWithValidation;
  error.validationErrors = result.validationErrors;
  return error;
}

// ─── Reads (LF-3215) ────────────────────────────────────────────────────

/**
 * Defensive contact normaliser — backend has occasionally returned
 * stringified contact objects mixed into the array. Tolerate both shapes.
 */
function normaliseContacts(raw: unknown[]): Contact[] {
  const out: Contact[] = [];
  for (const contact of raw || []) {
    if (
      typeof contact === 'object' &&
      contact !== null &&
      (contact as { id?: unknown }).id !== undefined
    ) {
      out.push(contact as Contact);
    } else if (typeof contact === 'string') {
      try {
        const parsed = JSON.parse(contact);
        if (parsed && typeof parsed === 'object' && parsed.id !== undefined) {
          console.warn('Parsed stringified contact from backend:', parsed);
          out.push(parsed as Contact);
        }
      } catch {
        console.error('Failed to parse stringified contact:', contact);
      }
    }
  }
  return out;
}

export async function fetchMatterContacts(
  matterId: number
): Promise<ContactListResponse> {
  const raw = await directApiClient.get<unknown[]>(
    `/matters/${matterId}/contacts`
  );
  const contacts = normaliseContacts(raw || []);
  return { contacts, total: contacts.length };
}

export async function fetchGlobalContact(
  contactId: number,
  options?: { includeSafeCustodyPackets?: boolean; includeMatters?: boolean }
): Promise<GlobalContact> {
  const includes: string[] = [];
  if (options?.includeSafeCustodyPackets) includes.push('safeCustodyPackets');
  if (options?.includeMatters) includes.push('matters');

  const params = new URLSearchParams();
  if (includes.length > 0) {
    params.set('include', includes.join(','));
  }
  const qs = params.toString();
  return directApiClient.get<GlobalContact>(
    `/contacts/${contactId}${qs ? `?${qs}` : ''}`
  );
}

export async function fetchBankVerificationDetails(
  matterId: number,
  verificationId: number
): Promise<BankVerificationDetails> {
  return directApiClient.get<BankVerificationDetails>(
    `/matters/${matterId}/contact-verifications/${verificationId}`
  );
}

// ─── Mutations ──────────────────────────────────────────────────────────

function prepareGlobalContactData(
  data: CreateContactRequest | UpdateContactRequest
): Record<string, unknown> {
  const {
    isLegalPersonalRepresentative: _isLegalPersonalRepresentative,
    isBeneficiary: _isBeneficiary,
    isClient: _isClient,
    isMinor: _isMinor,
    legalPersonalRepresentativeRole: _legalPersonalRepresentativeRole,
    ...coreData
  } = data;

  return coreData;
}

export async function createContactAction(
  matterId: number,
  data: CreateContactRequest
): Promise<ContactActionResult<Contact>> {
  try {
    const validationResult = CreateContactRequestSchema.safeParse(data);
    if (!validationResult.success) {
      return {
        success: false,
        error: 'Validation failed',
        validationErrors: validationResult.error.flatten().fieldErrors
      };
    }

    const requestData = { ...validationResult.data };
    if (requestData.dateOfBirth) {
      requestData.dateOfBirth = formatDateForApi(requestData.dateOfBirth);
    }

    const contact = await directApiClient.post<Contact>(
      `/matters/${matterId}/contacts`,
      requestData
    );
    return { success: true, data: contact };
  } catch (error) {
    return toErrorResult<Contact>(error);
  }
}

export async function updateContactAction(
  matterId: number,
  contactId: number,
  data: UpdateContactRequest
): Promise<ContactActionResult<Contact>> {
  try {
    const validationResult = UpdateContactRequestSchema.safeParse(data);
    if (!validationResult.success) {
      return {
        success: false,
        error: 'Validation failed',
        validationErrors: validationResult.error.flatten().fieldErrors
      };
    }

    const requestData = { ...validationResult.data };
    if (requestData.dateOfBirth) {
      requestData.dateOfBirth = formatDateForApi(requestData.dateOfBirth);
    }

    const contact = await directApiClient.put<Contact>(
      `/matters/${matterId}/contacts/${contactId}`,
      requestData
    );
    return { success: true, data: contact };
  } catch (error) {
    return toErrorResult<Contact>(error);
  }
}

export async function deleteContactAction(
  matterId: number,
  contactId: number
): Promise<ContactActionResult<void>> {
  try {
    await directApiClient.delete(`/matters/${matterId}/contacts/${contactId}`);
    return { success: true, data: undefined };
  } catch (error) {
    return toErrorResult<void>(error);
  }
}

// ============================================================================
// Global Contact Mutations (not matter-scoped)
// ============================================================================

export async function createGlobalContactAction(
  data: CreateContactRequest
): Promise<ContactActionResult<Contact>> {
  try {
    const requestData = prepareGlobalContactData(data);
    if (
      requestData.dateOfBirth &&
      typeof requestData.dateOfBirth === 'string'
    ) {
      requestData.dateOfBirth = formatDateForApi(requestData.dateOfBirth);
    }

    const contact = await directApiClient.post<Contact>(
      '/contacts',
      requestData
    );
    return { success: true, data: contact };
  } catch (error) {
    return toErrorResult<Contact>(error);
  }
}

export async function updateGlobalContactAction(
  contactId: number,
  data: UpdateContactRequest
): Promise<ContactActionResult<Contact>> {
  try {
    const requestData = prepareGlobalContactData(data);
    if (
      requestData.dateOfBirth &&
      typeof requestData.dateOfBirth === 'string'
    ) {
      requestData.dateOfBirth = formatDateForApi(requestData.dateOfBirth);
    }

    const contact = await directApiClient.put<Contact>(
      `/contacts/${contactId}`,
      requestData
    );
    return { success: true, data: contact };
  } catch (error) {
    return toErrorResult<Contact>(error);
  }
}

export async function deleteGlobalContactAction(
  contactId: number
): Promise<ContactActionResult<void>> {
  try {
    await directApiClient.delete(`/contacts/${contactId}`);
    return { success: true, data: undefined };
  } catch (error) {
    return toErrorResult<void>(error);
  }
}

/**
 * Record that a law-firm user has personally confirmed this contact's identity
 * **without** an identification document on file.
 *
 * Writes the identity axis only — it stamps `identificationStatus =
 * MANUALLY_CONFIRMED`, `identificationVerifiedAt`, the mandatory
 * `identificationConfirmationNote` and the acting user. It creates no
 * `ContactVerification` row and does **not** touch `isBankVerified` /
 * `bankAccount.isVerified`; bank verification is a separate axis.
 */
export async function confirmContactIdentityAction(
  matterId: number,
  contactId: number,
  data: ConfirmContactIdentityRequest
): Promise<ContactActionResult<Contact>> {
  try {
    const contact = await directApiClient.post<Contact>(
      `/matters/${matterId}/contacts/${contactId}/confirm-identity`,
      data
    );
    return { success: true, data: contact };
  } catch (error) {
    return toErrorResult<Contact>(error);
  }
}

// ============================================================================
// Ready-to-Pay Mutations
// ============================================================================

/**
 * Explicitly confirm a beneficiary is ready to pay — the lawyer checkpoint that
 * drives Ready-to-Pay membership in the reworked beneficiary tab. The file
 * `note` is mandatory (the backend rejects an empty/whitespace note) and is the
 * audit record surfaced on the beneficiary's Ready-to-Pay view. Eligibility is
 * gated server-side (`canMarkReadyToPay`): an ineligible beneficiary returns 409
 * `NOT_ELIGIBLE_FOR_PAYMENT`. Returns the updated `Contact`.
 */
export async function markReadyToPayAction(
  matterId: number,
  contactId: number,
  data: { note: string }
): Promise<ContactActionResult<Contact>> {
  try {
    const contact = await directApiClient.post<Contact>(
      `/matters/${matterId}/contacts/${contactId}/mark-ready-to-pay`,
      data
    );
    return { success: true, data: contact };
  } catch (error) {
    return toErrorResult<Contact>(error);
  }
}

/**
 * Clear a beneficiary's ready-to-pay confirmation (demote back to Review
 * Details). The reason is optional — it rides the audit event only; the stored
 * mark is cleared regardless. A no-op when the beneficiary is already not ready.
 * Returns the updated `Contact`. (Consumed by BENTAB-7's Ready-to-Pay drawer.)
 */
export async function markNotReadyToPayAction(
  matterId: number,
  contactId: number,
  data: { note?: string }
): Promise<ContactActionResult<Contact>> {
  try {
    const contact = await directApiClient.post<Contact>(
      `/matters/${matterId}/contacts/${contactId}/mark-not-ready-to-pay`,
      data
    );
    return { success: true, data: contact };
  } catch (error) {
    return toErrorResult<Contact>(error);
  }
}

// ============================================================================
// Bank Verification Mutations
// ============================================================================

export async function requestBankVerificationAction(
  matterId: number,
  contactId: number,
  emailOverride?: BeneficiaryEmailOverride,
  attachments?: BeneficiaryAttachmentSelection[],
  sendCopyToUser?: boolean
): Promise<ContactActionResult<RequestBankVerificationResponse>> {
  try {
    const response =
      await directApiClient.post<RequestBankVerificationResponse>(
        `/matters/${matterId}/contact-verifications/request`,
        { contactId, emailOverride, attachments, sendCopyToUser }
      );

    return { success: true, data: response };
  } catch (error) {
    return toErrorResult<RequestBankVerificationResponse>(error);
  }
}

/**
 * Render the beneficiary portal-access-link email for preview/edit before sending. Returns the
 * resolved subject + body HTML from the firm's effective template. The backend returns 404 only
 * when the contact or matter cannot be found. `components` mirrors the send selection so the
 * bundled-vs-standalone subject matches what will be sent.
 */
export async function previewBeneficiaryEmailAction(
  matterId: number,
  contactId: number,
  components?: { bankVerification?: boolean; declaration?: boolean }
): Promise<ContactActionResult<PreviewBeneficiaryEmailResponse>> {
  try {
    const response =
      await directApiClient.post<PreviewBeneficiaryEmailResponse>(
        `/matters/${matterId}/contact-verifications/preview`,
        {
          contactId,
          bankVerification: components?.bankVerification,
          declaration: components?.declaration
        }
      );

    return { success: true, data: response };
  } catch (error) {
    return toErrorResult<PreviewBeneficiaryEmailResponse>(error);
  }
}

export async function resendVerificationLinkAction(
  matterId: number,
  verificationId: number
): Promise<ContactActionResult<ResendVerificationLinkResponse>> {
  try {
    const response = await directApiClient.post<ResendVerificationLinkResponse>(
      `/matters/${matterId}/contact-verifications/${verificationId}/resend`,
      {}
    );

    return { success: true, data: response };
  } catch (error) {
    return toErrorResult<ResendVerificationLinkResponse>(error);
  }
}

export async function approveBankVerificationAction(
  matterId: number,
  verificationId: number,
  request: ApproveBankVerificationRequest
): Promise<ContactActionResult<ApproveBankVerificationResponse>> {
  try {
    const response =
      await directApiClient.post<ApproveBankVerificationResponse>(
        `/matters/${matterId}/contact-verifications/${verificationId}/approve`,
        request
      );

    return { success: true, data: response };
  } catch (error) {
    return toErrorResult<ApproveBankVerificationResponse>(error);
  }
}

/**
 * What merging `mergeContactId` into `survivorId` would do — read-only, writes
 * nothing. Call this before every merge: it is what turns a destroyed
 * distribution declaration from a surprise into a decision.
 */
export async function previewContactMergeAction(
  survivorId: number,
  mergeContactId: number
): Promise<ContactActionResult<ContactMergePreview>> {
  try {
    const response = await directApiClient.get<ContactMergePreview>(
      `/contacts/${survivorId}/merge-preview?mergeContactId=${mergeContactId}`
    );
    return { success: true, data: response };
  } catch (error) {
    return toErrorResult<ContactMergePreview>(error);
  }
}

/**
 * Merge `mergeContactId` into `survivorId`. `fieldOverrides` names the fields to
 * take from the record being merged away; fields the survivor leaves empty are
 * carried across automatically and need not be listed.
 */
export async function mergeContactsAction(
  survivorId: number,
  mergeContactId: number,
  fieldOverrides: string[] = []
): Promise<ContactActionResult<ContactMergeResult>> {
  try {
    const response = await directApiClient.post<ContactMergeResult>(
      `/contacts/${survivorId}/merge`,
      { mergeContactId, fieldOverrides }
    );
    return { success: true, data: response };
  } catch (error) {
    return toErrorResult<ContactMergeResult>(error);
  }
}

/**
 * The firm's duplicate worklist, strongest match first. Read-only — nothing here
 * merges without a lawyer confirming it.
 */
export async function fetchContactDuplicates(): Promise<
  ContactActionResult<ContactDuplicateCandidate[]>
> {
  try {
    const response = await directApiClient.get<ContactDuplicateCandidate[]>(
      '/contacts/duplicates'
    );
    return { success: true, data: response };
  } catch (error) {
    return toErrorResult<ContactDuplicateCandidate[]>(error);
  }
}

/**
 * Record that a suggested pair is genuinely two different people, so it stops
 * being offered. Order-independent and idempotent.
 */
export async function dismissContactDuplicateAction(
  contactId: number,
  otherContactId: number
): Promise<ContactActionResult<void>> {
  try {
    await directApiClient.post(`/contacts/${contactId}/duplicates/dismiss`, {
      otherContactId
    });
    return { success: true, data: undefined };
  } catch (error) {
    return toErrorResult<void>(error);
  }
}
